import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';
import {
  Search,
  BarChart3,
  Zap,
  Shield,
  MessageSquare,
  Target,
  TrendingUp,
  CheckCircle2,
  Star,
  Globe,
  Smartphone,
} from 'lucide-react';

const Features: React.FC = () => {
  const features = [
    {
      icon: <Search className="w-8 h-8" />,
      title: 'AI-Powered KOL Discovery',
      description: 'Find the perfect influencers using our advanced AI matching algorithm. Filter by demographics, engagement rates, audience insights, and content style.',
      color: 'from-violet-500 to-purple-600',
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Real-Time Analytics',
      description: 'Track campaign performance with comprehensive analytics. Monitor engagement, reach, ROI, and audience growth in real-time dashboards.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: 'Seamless Communication',
      description: 'Built-in messaging system for direct brand-KOL communication. Share scripts, provide feedback, and manage collaborations efficiently.',
      color: 'from-emerald-500 to-teal-500',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Contract Management',
      description: 'Digital contracts with e-signatures. Milestone-based payments, compliance checklists, and transparent terms protect both parties.',
      color: 'from-orange-500 to-amber-500',
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Audience Targeting',
      description: 'Define your target audience with precision. Our AI analyzes follower demographics to match brands with the most relevant KOLs.',
      color: 'from-pink-500 to-rose-500',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Campaign Tracking',
      description: 'Monitor campaign progress from kickoff to completion. Track deliverables, deadlines, and content approvals in one centralized platform.',
      color: 'from-indigo-500 to-violet-500',
    },
  ];

  const additionalFeatures = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'AI Script Generation',
      description: 'Leverage DeepSeek AI to generate optimized content scripts tailored to each KOL\'s style and audience.',
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Multi-Platform Support',
      description: 'Manage campaigns across Instagram, TikTok, and YouTube from a single dashboard.',
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'Mobile Optimized',
      description: 'Full-featured mobile app for KOLs to manage submissions and communicate on-the-go.',
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: 'KOL Verification',
      description: 'Verified follower counts and engagement metrics to ensure authenticity.',
    },
  ];

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="relative px-6 py-24 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-6xl mx-auto text-center"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent">
            Powerful Features for
            <br />Influencer Marketing Success
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Everything you need to discover, connect, and collaborate with the perfect KOLs
            for your brand. From AI-powered matching to comprehensive analytics.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/login"
              className="rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 px-8 py-3 font-semibold transition-opacity hover:opacity-90"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="rounded-lg border border-white/10 px-8 py-3 font-semibold transition-colors hover:bg-white/10"
            >
              Watch Demo
            </Link>
          </div>
        </motion.div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Core Platform Features</h2>
            <p className="text-gray-400 text-lg">
              Streamline your entire influencer marketing workflow
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-800 hover:border-gray-700 transition-colors group"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <div className="text-white">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={3} className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">And Much More...</h2>
            <p className="text-gray-400 text-lg">
              Additional tools to supercharge your campaigns
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 rounded-xl bg-gray-800/50 border border-gray-800 hover:border-gray-700 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                  <div className="text-violet-400">
                    {feature.icon}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={4} className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-6">
                Why Choose myKOL?
              </h2>
              <div className="space-y-4">
                {[
                  'Save time with AI-powered matching',
                  'Increase ROI with data-driven decisions',
                  'Build long-term relationships with KOLs',
                  'Ensure compliance and transparency',
                  'Scale your influencer campaigns effortlessly',
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-blue-600 rounded-2xl blur-3xl opacity-20" />
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="text-center">
                  <div className="text-6xl font-bold bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent mb-2">
                    100+
                  </div>
                  <div className="text-gray-400 mb-6">Brands Trusting myKOL</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-white">500+</div>
                      <div className="text-sm text-gray-500">Active KOLs</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-white">1M+</div>
                      <div className="text-sm text-gray-500">Reach per Campaign</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={5} className="px-6 py-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-violet-600/20 to-blue-600/20 rounded-3xl p-12 border border-violet-600/30"
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-gray-400 text-lg mb-8">
              Join 100+ brands already using myKOL to revolutionize their influencer marketing
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/login"
                className="rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 px-8 py-4 text-lg font-semibold transition-opacity hover:opacity-90"
              >
                Start Your Free Trial
              </Link>
              <Link
                to="/contact"
                className="rounded-lg border border-white/10 px-8 py-4 text-lg font-semibold transition-colors hover:bg-white/10"
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Features;
